import { useState, useEffect, useCallback } from 'react';
import { analyticsAPI, zohoAPI } from '../lib/api';
import { formatNumber, formatCurrency, formatDateTime } from '../lib/utils';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Badge } from '../components/ui/badge';
import { Button } from '../components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { toast } from 'sonner';
import {
  TrendingUp,
  Users,
  ShoppingCart,
  DollarSign,
  Award,
  Loader2,
  RefreshCw,
  Trophy,
  Medal,
  ArrowUpRight,
  ArrowDownRight,
  Download,
} from 'lucide-react';

export default function PerformancePage() {
  const [summary, setSummary] = useState(null);
  const [buyers, setBuyers] = useState([]);
  const [sellers, setSellers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [syncing, setSyncing] = useState(false);
  const [buyerSort, setBuyerSort] = useState('total_cost');
  const [sellerSort, setSellerSort] = useState('total_revenue');

  const fetchSummary = useCallback(async () => {
    try {
      const response = await analyticsAPI.getSummary();
      setSummary(response.data);
    } catch (error) {
      console.error('Failed to fetch summary:', error);
    }
  }, []);

  const fetchBuyers = useCallback(async () => {
    try {
      const response = await analyticsAPI.getBuyerPerformance({ sort_by: buyerSort });
      setBuyers(response.data.buyers || []);
    } catch (error) {
      console.error('Failed to fetch buyers:', error);
    }
  }, [buyerSort]);

  const fetchSellers = useCallback(async () => {
    try {
      const response = await analyticsAPI.getSellerPerformance({ sort_by: sellerSort });
      setSellers(response.data.sellers || []);
    } catch (error) {
      console.error('Failed to fetch sellers:', error);
    }
  }, [sellerSort]);

  const fetchAllData = useCallback(async () => {
    setLoading(true);
    await Promise.all([fetchSummary(), fetchBuyers(), fetchSellers()]);
    setLoading(false);
  }, [fetchSummary, fetchBuyers, fetchSellers]);

  useEffect(() => {
    fetchAllData();
  }, [fetchAllData]);

  useEffect(() => {
    fetchBuyers();
  }, [buyerSort, fetchBuyers]);

  useEffect(() => {
    fetchSellers();
  }, [sellerSort, fetchSellers]);

  const handleSyncPurchasePersons = async () => {
    setSyncing(true);
    try {
      toast.info('Syncing purchase person data from Zoho (this may take a few minutes)...');
      const response = await zohoAPI.syncPurchasePersons(500);
      toast.success(`Synced ${response.data.with_person_data || 0} POs with purchase person data`);
      await fetchAllData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to sync purchase person data');
    } finally {
      setSyncing(false);
    }
  };

  const getRankBadge = (rank) => {
    if (rank === 1) return <Trophy className="h-5 w-5 text-amber-500" />;
    if (rank === 2) return <Medal className="h-5 w-5 text-gray-400" />;
    if (rank === 3) return <Medal className="h-5 w-5 text-amber-700" />;
    return <span className="text-muted-foreground font-mono">#{rank}</span>;
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="performance-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold tracking-tight flex items-center gap-2">
            <TrendingUp className="h-6 w-6 text-primary" />
            Performance Analytics
          </h1>
          <p className="text-muted-foreground">
            Buyer and seller performance rankings from Zoho data
          </p>
        </div>
        <div className="flex gap-2">
          <Button
            variant="outline"
            onClick={() => fetchAllData()}
            disabled={loading || syncing}
            className="rounded-sm"
            data-testid="refresh-btn"
          >
            <RefreshCw className={`h-4 w-4 mr-2 ${loading ? 'animate-spin' : ''}`} />
            Refresh
          </Button>
          <Button
            onClick={handleSyncPurchasePersons}
            disabled={loading || syncing}
            className="rounded-sm"
            data-testid="sync-persons-btn"
          >
            {syncing ? (
              <Loader2 className="h-4 w-4 animate-spin mr-2" />
            ) : (
              <Download className="h-4 w-4 mr-2" />
            )}
            Sync Purchase Persons
          </Button>
        </div>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <Card className="rounded-sm">
          <CardContent className="p-4">
            <div className="flex items-center gap-2 text-muted-foreground mb-1">
              <Users className="h-4 w-4" />
              <span className="text-xs font-medium uppercase">Active Buyers</span>
            </div>
            <p className="text-2xl font-bold font-mono">{summary?.buyers?.unique_buyers || 0}</p>
            <p className="text-xs text-muted-foreground mt-1">
              {formatNumber(summary?.buyers?.total_pos || 0)} purchase orders
            </p>
          </CardContent>
        </Card>

        <Card className="rounded-sm">
          <CardContent className="p-4">
            <div className="flex items-center gap-2 text-muted-foreground mb-1">
              <ShoppingCart className="h-4 w-4" />
              <span className="text-xs font-medium uppercase">Total PO Value</span>
            </div>
            <p className="text-2xl font-bold font-mono">{formatCurrency(summary?.buyers?.total_po_value || 0)}</p>
          </CardContent>
        </Card>

        <Card className="rounded-sm">
          <CardContent className="p-4">
            <div className="flex items-center gap-2 text-muted-foreground mb-1">
              <Users className="h-4 w-4" />
              <span className="text-xs font-medium uppercase">Active Sellers</span>
            </div>
            <p className="text-2xl font-bold font-mono">{summary?.sellers?.unique_sellers || 0}</p>
            <p className="text-xs text-muted-foreground mt-1">
              {formatNumber(summary?.sellers?.total_sos || 0)} sales orders
            </p>
          </CardContent>
        </Card>

        <Card className="rounded-sm">
          <CardContent className="p-4">
            <div className="flex items-center gap-2 text-muted-foreground mb-1">
              <DollarSign className="h-4 w-4" />
              <span className="text-xs font-medium uppercase">Total SO Value</span>
            </div>
            <p className="text-2xl font-bold font-mono">{formatCurrency(summary?.sellers?.total_so_value || 0)}</p>
          </CardContent>
        </Card>
      </div>

      {/* Top Performers Highlight */}
      {(summary?.top_buyer || summary?.top_seller) && (
        <div className="grid md:grid-cols-2 gap-4">
          {summary?.top_buyer && (
            <Card className="rounded-sm border-amber-200 bg-amber-50/50">
              <CardContent className="p-4">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-full bg-amber-100 flex items-center justify-center">
                      <Trophy className="h-5 w-5 text-amber-600" />
                    </div>
                    <div>
                      <p className="text-xs font-medium text-amber-600 uppercase">Top Buyer</p>
                      <p className="font-semibold">{summary.top_buyer.name}</p>
                    </div>
                  </div>
                  <div className="text-right">
                    <p className="text-lg font-bold font-mono">{formatCurrency(summary.top_buyer.total_value)}</p>
                    <p className="text-xs text-muted-foreground">Total PO Value</p>
                  </div>
                </div>
              </CardContent>
            </Card>
          )}
          {summary?.top_seller && (
            <Card className="rounded-sm border-emerald-200 bg-emerald-50/50">
              <CardContent className="p-4">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-full bg-emerald-100 flex items-center justify-center">
                      <Trophy className="h-5 w-5 text-emerald-600" />
                    </div>
                    <div>
                      <p className="text-xs font-medium text-emerald-600 uppercase">Top Seller</p>
                      <p className="font-semibold">{summary.top_seller.name}</p>
                    </div>
                  </div>
                  <div className="text-right">
                    <p className="text-lg font-bold font-mono">{formatCurrency(summary.top_seller.total_value)}</p>
                    <p className="text-xs text-muted-foreground">Total SO Value</p>
                  </div>
                </div>
              </CardContent>
            </Card>
          )}
        </div>
      )}

      {/* Performance Tables */}
      <Tabs defaultValue="buyers" className="space-y-4">
        <TabsList>
          <TabsTrigger value="buyers" data-testid="buyers-tab">
            <ShoppingCart className="h-4 w-4 mr-2" />
            Buyer Rankings ({buyers.length})
          </TabsTrigger>
          <TabsTrigger value="sellers" data-testid="sellers-tab">
            <DollarSign className="h-4 w-4 mr-2" />
            Seller Rankings ({sellers.length})
          </TabsTrigger>
        </TabsList>

        {/* Buyers Tab */}
        <TabsContent value="buyers">
          <Card className="rounded-sm">
            <CardHeader className="pb-3">
              <div className="flex items-center justify-between">
                <div>
                  <CardTitle className="text-lg">Buyer Performance</CardTitle>
                  <CardDescription>Purchase persons ranked by procurement metrics</CardDescription>
                </div>
                <Select value={buyerSort} onValueChange={setBuyerSort}>
                  <SelectTrigger className="w-[180px] rounded-sm" data-testid="buyer-sort">
                    <SelectValue placeholder="Sort by" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="total_cost">Total Value</SelectItem>
                    <SelectItem value="total_qty">Total Quantity</SelectItem>
                    <SelectItem value="order_count">Order Count</SelectItem>
                    <SelectItem value="avg_order_value">Avg Order Value</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </CardHeader>
            <CardContent className="p-0">
              <Table>
                <TableHeader>
                  <TableRow className="bg-muted/50">
                    <TableHead className="w-16 font-medium text-xs uppercase">Rank</TableHead>
                    <TableHead className="font-medium text-xs uppercase">Buyer Name</TableHead>
                    <TableHead className="font-medium text-xs uppercase text-right">Total Orders</TableHead>
                    <TableHead className="font-medium text-xs uppercase text-right">Total Qty</TableHead>
                    <TableHead className="font-medium text-xs uppercase text-right">Total Value</TableHead>
                    <TableHead className="font-medium text-xs uppercase text-right">Avg Order</TableHead>
                    <TableHead className="font-medium text-xs uppercase text-right">Vendors</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {buyers.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={7} className="text-center py-8">
                        <Users className="h-12 w-12 mx-auto text-muted-foreground/50 mb-2" />
                        <p className="text-muted-foreground">
                          No buyer data available. Sync purchase orders from Zoho.
                        </p>
                      </TableCell>
                    </TableRow>
                  ) : (
                    buyers.map((buyer) => (
                      <TableRow key={buyer.buyer_name} className="table-row-hover">
                        <TableCell className="text-center">{getRankBadge(buyer.rank)}</TableCell>
                        <TableCell className="font-medium">{buyer.buyer_name}</TableCell>
                        <TableCell className="font-mono text-right">{formatNumber(buyer.total_orders)}</TableCell>
                        <TableCell className="font-mono text-right">{formatNumber(buyer.total_qty)}</TableCell>
                        <TableCell className="font-mono text-right font-semibold">
                          {formatCurrency(buyer.total_value)}
                        </TableCell>
                        <TableCell className="font-mono text-right">{formatCurrency(buyer.avg_order_value)}</TableCell>
                        <TableCell className="font-mono text-right">{buyer.unique_vendors}</TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Sellers Tab */}
        <TabsContent value="sellers">
          <Card className="rounded-sm">
            <CardHeader className="pb-3">
              <div className="flex items-center justify-between">
                <div>
                  <CardTitle className="text-lg">Seller Performance</CardTitle>
                  <CardDescription>Sales persons ranked by sales metrics</CardDescription>
                </div>
                <Select value={sellerSort} onValueChange={setSellerSort}>
                  <SelectTrigger className="w-[180px] rounded-sm" data-testid="seller-sort">
                    <SelectValue placeholder="Sort by" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="total_revenue">Total Revenue</SelectItem>
                    <SelectItem value="order_count">Order Count</SelectItem>
                    <SelectItem value="avg_order_value">Avg Order Value</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </CardHeader>
            <CardContent className="p-0">
              <Table>
                <TableHeader>
                  <TableRow className="bg-muted/50">
                    <TableHead className="w-16 font-medium text-xs uppercase">Rank</TableHead>
                    <TableHead className="font-medium text-xs uppercase">Seller Name</TableHead>
                    <TableHead className="font-medium text-xs uppercase text-right">Total Orders</TableHead>
                    <TableHead className="font-medium text-xs uppercase text-right">Total Revenue</TableHead>
                    <TableHead className="font-medium text-xs uppercase text-right">Avg Order</TableHead>
                    <TableHead className="font-medium text-xs uppercase text-right">Customers</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {sellers.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={6} className="text-center py-8">
                        <Users className="h-12 w-12 mx-auto text-muted-foreground/50 mb-2" />
                        <p className="text-muted-foreground">
                          No seller data available. Sync sales orders from Zoho.
                        </p>
                      </TableCell>
                    </TableRow>
                  ) : (
                    sellers.map((seller) => (
                      <TableRow key={seller.seller_name} className="table-row-hover">
                        <TableCell className="text-center">{getRankBadge(seller.rank)}</TableCell>
                        <TableCell className="font-medium">{seller.seller_name}</TableCell>
                        <TableCell className="font-mono text-right">{formatNumber(seller.total_orders)}</TableCell>
                        <TableCell className="font-mono text-right font-semibold">
                          {formatCurrency(seller.total_revenue)}
                        </TableCell>
                        <TableCell className="font-mono text-right">{formatCurrency(seller.avg_order_value)}</TableCell>
                        <TableCell className="font-mono text-right">{seller.unique_customers}</TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
}
